import { useEffect, useMemo } from 'react';
import {
  applyWorkspaceAutoRedirectPlanSideEffects,
  resolveWorkspaceAutoRedirectPlan,
} from './resolveWorkspaceAutoRedirect';
import type { UseWorkspaceAutoRedirectsParams } from './useWorkspaceAutoRedirects.types';

const useWorkspaceAutoRedirects = ({
  fetchCompanyInfo,
  fetchFundInfo,
  hasNotFirmPermissions,
  isCompanyProduct,
  locationState,
  lowerPermissions,
  markRestrictedUser,
  navigate,
  resolvedFirmList,
  resolvedStandaloneCompaniesPermissions,
  resolvedUser,
}: UseWorkspaceAutoRedirectsParams) => {
  const redirectPlan = useMemo(() => resolveWorkspaceAutoRedirectPlan({
    hasNotFirmPermissions,
    isCompanyProduct,
    locationState,
    lowerPermissions,
    resolvedFirmList,
    resolvedStandaloneCompaniesPermissions,
    resolvedUser,
  }), [
    hasNotFirmPermissions,
    isCompanyProduct,
    locationState,
    lowerPermissions,
    resolvedFirmList,
    resolvedStandaloneCompaniesPermissions,
    resolvedUser,
  ]);

  // Redirect priority is intentional: explicit deep links first, then saved resume state, then deterministic single-option routing, then restricted fallback.
  useEffect(() => {
    if (!redirectPlan) {
      return undefined;
    }

    let shouldNavigate = true;
    const applySideEffectsAndNavigate = async () => {
      await applyWorkspaceAutoRedirectPlanSideEffects({
        fetchCompanyInfo,
        fetchFundInfo,
        markRestrictedUser,
        plan: redirectPlan,
      });

      if (!shouldNavigate || redirectPlan.kind === 'clear-app-auto-select') {
        return;
      }

      navigate(redirectPlan.target);
    };

    applySideEffectsAndNavigate();

    return () => {
      shouldNavigate = false;
    };
  }, [
    fetchCompanyInfo,
    fetchFundInfo,
    markRestrictedUser,
    navigate,
    redirectPlan,
  ]);

  return redirectPlan;
};

export default useWorkspaceAutoRedirects;
